/**
 * Admin — Wellness Gateway Leads
 *
 * Additive admin section. Mounts at /admin/wellness-gateway-leads.
 * Uses the existing KCIS admin Bearer token (via `api` axios instance) to call
 * the Phase 1 endpoints:
 *
 *   GET    /api/admin/wellness-leads?pathway=&follow_up_status=&funnel=&q=&page=&page_size=
 *   GET    /api/admin/wellness-leads/stats
 *   GET    /api/admin/wellness-leads/{id}
 *   PATCH  /api/admin/wellness-leads/{id}
 *   GET    /api/admin/wellness-leads/export.csv?pathway=&follow_up_status=
 *
 * Visibility: super_admin only (per ADMIN_SPEC). Non-super_admins get redirected
 * back to the dashboard. KCIS Lite/Pro practitioner flows are NOT modified.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, adminAuth, API } from "@/lib/api";
import { TopNav, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Leaf, LogOut, Loader2, Search, Download, Copy, ExternalLink, ChevronLeft, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

const PATHWAY_OPTIONS = [
  { value: "all", label: "All pathways" },
  { value: "clinic", label: "Clinic" },
  { value: "coaching", label: "Coaching" },
  { value: "university", label: "University" },
  { value: "comfort_care", label: "Comfort Care" },
  { value: "hybrid", label: "Hybrid" },
  { value: "kcis", label: "KCIS" },
];
const FOLLOW_UP_OPTIONS = [
  { value: "all", label: "All follow-up" },
  { value: "new", label: "new" },
  { value: "contacted", label: "contacted" },
  { value: "qualified", label: "qualified" },
  { value: "converted", label: "converted" },
  { value: "archived", label: "archived" },
];
const FUNNEL_OPTIONS = [
  { value: "all", label: "All funnels" },
  { value: "kcis_clinical_assessment", label: "kcis_clinical_assessment" },
  { value: "coaching_funnel", label: "coaching_funnel" },
  { value: "university_pathway", label: "university_pathway" },
  { value: "comfort_care_inquiry", label: "comfort_care_inquiry" },
  { value: "clinic_external", label: "clinic_external" },
];

const PATHWAY_PILL = {
  clinic: "bg-emerald-50 text-emerald-700 border-emerald-200",
  coaching: "bg-rose-50 text-rose-700 border-rose-200",
  university: "bg-blue-50 text-blue-700 border-blue-200",
  comfort_care: "bg-amber-50 text-amber-800 border-amber-200",
  hybrid: "bg-purple-50 text-purple-800 border-purple-200",
  kcis: "bg-purple-900 text-white border-purple-900",
};

const FOLLOW_UP_PILL = {
  new: "bg-slate-100 text-slate-700 border-slate-200",
  contacted: "bg-amber-50 text-amber-800 border-amber-200",
  qualified: "bg-blue-50 text-blue-700 border-blue-200",
  converted: "bg-emerald-50 text-emerald-700 border-emerald-200",
  archived: "bg-zinc-100 text-zinc-500 border-zinc-200",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}

function PathwayPill({ pathway }) {
  return (
    <span
      className={`text-xs rounded-full px-3 py-1 border whitespace-nowrap ${PATHWAY_PILL[pathway] || "bg-slate-100 text-slate-700 border-slate-200"}`}
      data-testid={`pathway-pill-${pathway}`}
    >
      {pathway}
    </span>
  );
}

function FollowUpPill({ status }) {
  return (
    <span className={`text-xs rounded-full px-3 py-1 border whitespace-nowrap ${FOLLOW_UP_PILL[status] || FOLLOW_UP_PILL.new}`}>
      {status}
    </span>
  );
}

function ScoreBar({ label, value, max = 12 }) {
  const pct = Math.min(100, Math.round((value / max) * 100));
  return (
    <div className="flex items-center gap-3" data-testid={`score-bar-${label.toLowerCase()}`}>
      <div className="w-28 text-xs text-slate-600 uppercase tracking-wide font-medium">{label}</div>
      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full bg-purple-900" style={{ width: `${pct}%` }} />
      </div>
      <div className="w-8 text-right text-sm font-medium text-slate-900">{value}</div>
    </div>
  );
}

/* ───────── Detail drawer ───────── */
function LeadDrawer({ leadId, open, onClose, onPatched, questionsCache }) {
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [notes, setNotes] = useState("");
  const [admins, setAdmins] = useState([]);

  useEffect(() => {
    if (!open || !leadId) return;
    let cancel = false;
    setLoading(true);
    api.get(`/admin/wellness-leads/${leadId}`)
      .then(({ data }) => { if (!cancel) { setLead(data); setNotes(data.follow_up_notes || ""); } })
      .catch(() => toast.error("Could not load lead"))
      .finally(() => { if (!cancel) setLoading(false); });
    // Load admins for "assigned to"
    api.get(`/admin/practitioners`)
      .then(({ data }) => { if (!cancel) setAdmins(data || []); })
      .catch(() => {});
    return () => { cancel = true; };
  }, [open, leadId]);

  const patch = async (body) => {
    try {
      const { data } = await api.patch(`/admin/wellness-leads/${leadId}`, body);
      setLead(data);
      onPatched && onPatched(data);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const saveNotesDebounced = useRef(null);
  const onNotesBlur = async () => {
    if (notes === (lead?.follow_up_notes || "")) return;
    setSavingNotes(true);
    await patch({ follow_up_notes: notes });
    setSavingNotes(false);
  };

  const sendToKcisFunnel = async () => {
    try {
      await fetch(`${API}/wellness-assessment/${leadId}/funnel-clicked`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ funnel_key: "kcis_clinical_assessment" }),
      });
      window.open("/clinical-assessment?source=wellness_gateway", "_blank");
      toast.success("Marked as routed to KCIS Clinical Assessment");
    } catch {
      toast.error("Could not mark funnel click");
    }
  };

  const copyJson = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(lead, null, 2));
      toast.success("Copied JSON to clipboard");
    } catch { toast.error("Clipboard copy failed"); }
  };

  // Build "questions answered" view by joining questions definition + lead answers
  const sectionedQuestions = useMemo(() => {
    if (!questionsCache || !lead) return {};
    const out = {};
    for (const q of questionsCache.questions.questions || []) {
      if (!out[q.section]) out[q.section] = [];
      const raw = lead[q.airtable_key];
      let renderedAnswer = "—";
      if (raw == null || (Array.isArray(raw) && raw.length === 0) || raw === "") {
        renderedAnswer = "—";
      } else if (Array.isArray(raw)) {
        renderedAnswer = raw.join(" · ");
      } else if (q.type === "slider") {
        const labels = q.scale_labels || {};
        renderedAnswer = `${raw} (1=${labels["1"] || labels[1] || "low"} · 5=${labels["5"] || labels[5] || "high"})`;
      } else {
        renderedAnswer = String(raw);
      }
      out[q.section].push({ q, renderedAnswer });
    }
    return out;
  }, [questionsCache, lead]);

  const sectionTitles = questionsCache?.questions?.section_titles || {};

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="wellness-lead-drawer">
        {loading || !lead ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-purple-900" />
          </div>
        ) : (
          <>
            <SheetHeader>
              <SheetTitle className="font-display text-2xl text-slate-900">{lead.first_name}</SheetTitle>
              <SheetDescription>
                <a href={`mailto:${lead.email}`} className="text-purple-900 underline">{lead.email}</a>
                <span className="text-slate-500"> · created {fmtDate(lead.created_at)}</span>
              </SheetDescription>
            </SheetHeader>

            <div className="mt-6 flex flex-wrap gap-2">
              <PathwayPill pathway={lead.pathway_assigned} />
              <FollowUpPill status={lead.follow_up_status} />
              <Badge variant="outline" className="text-xs">funnel: {lead.recommended_funnel || "—"}</Badge>
              <Badge variant="outline" className="text-xs">{lead.intake_type}</Badge>
            </div>

            {/* Scores breakdown */}
            <section className="mt-8">
              <div className="kcis-overline text-purple-900 mb-3">Scores breakdown</div>
              <div className="space-y-2">
                <ScoreBar label="Clinic" value={lead.score_clinic ?? 0} />
                <ScoreBar label="Coaching" value={lead.score_coaching ?? 0} />
                <ScoreBar label="University" value={lead.score_university ?? 0} />
                <ScoreBar label="Comfort Care" value={lead.score_comfort_care ?? 0} />
                <ScoreBar label="KCIS" value={lead.score_kcis ?? 0} />
              </div>
            </section>

            {/* Vision quote */}
            {lead.q15_success_vision && (
              <section className="mt-8 border-l-4 border-purple-900 bg-purple-50/40 p-4 rounded-r-lg">
                <div className="kcis-overline text-purple-900 mb-1">Their vision (Q15)</div>
                <blockquote className="font-display italic text-slate-800 text-lg leading-relaxed">
                  “{lead.q15_success_vision}”
                </blockquote>
              </section>
            )}

            {/* Follow-up panel */}
            <section className="mt-8 kcis-card p-5" data-testid="follow-up-panel">
              <div className="kcis-overline text-purple-900 mb-3">Follow-up</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-slate-500 uppercase tracking-wide">Status</label>
                  <Select
                    value={lead.follow_up_status || "new"}
                    onValueChange={(v) => patch({ follow_up_status: v })}
                  >
                    <SelectTrigger className="mt-1" data-testid="follow-up-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FOLLOW_UP_OPTIONS.filter(o => o.value !== "all").map(o => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-slate-500 uppercase tracking-wide">Assigned to</label>
                  <Select
                    value={lead.follow_up_assigned_to || "__none__"}
                    onValueChange={(v) => patch({ follow_up_assigned_to: v === "__none__" ? null : v })}
                  >
                    <SelectTrigger className="mt-1" data-testid="follow-up-assigned-select">
                      <SelectValue placeholder="Unassigned" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Unassigned</SelectItem>
                      {admins.map(a => (
                        <SelectItem key={a.id} value={a.email}>{a.email}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-4">
                <label className="text-xs text-slate-500 uppercase tracking-wide">Notes</label>
                <textarea
                  className="mt-1 w-full rounded-md border border-slate-300 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-900"
                  rows={3}
                  value={notes}
                  data-testid="follow-up-notes"
                  onChange={(e) => setNotes(e.target.value)}
                  onBlur={onNotesBlur}
                  placeholder="Internal follow-up notes…"
                />
                {savingNotes && <p className="text-xs text-slate-500 mt-1">Saving…</p>}
              </div>
            </section>

            {/* Answers accordion */}
            <section className="mt-8">
              <div className="kcis-overline text-purple-900 mb-3">All answers</div>
              <Accordion type="multiple" defaultValue={[]} className="border rounded-lg">
                {Object.keys(sectionedQuestions).sort().map((sec) => (
                  <AccordionItem key={sec} value={`section-${sec}`}>
                    <AccordionTrigger className="px-4 text-left">
                      Section {sec} — {sectionTitles[sec] || ""}
                    </AccordionTrigger>
                    <AccordionContent className="px-4">
                      <dl className="space-y-3">
                        {sectionedQuestions[sec].map(({ q, renderedAnswer }) => (
                          <div key={q.id}>
                            <dt className="text-xs uppercase tracking-wide text-slate-500">{q.id.toUpperCase()} · {q.prompt}</dt>
                            <dd className="mt-0.5 text-sm text-slate-800">{renderedAnswer}</dd>
                          </div>
                        ))}
                      </dl>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>

            {/* Footer actions */}
            <div className="mt-8 flex flex-wrap gap-3 sticky bottom-0 bg-white py-4 border-t">
              <Button onClick={sendToKcisFunnel} className="kcis-btn-primary" data-testid="send-to-kcis-btn">
                <ExternalLink className="h-4 w-4 mr-2" /> Send to KCIS Clinical Assessment
              </Button>
              <Button variant="outline" onClick={copyJson} data-testid="copy-lead-json">
                <Copy className="h-4 w-4 mr-2" /> Copy JSON
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

/* ───────── Page shell ───────── */
export default function AdminWellnessGatewayLeads() {
  const navigate = useNavigate();
  const isSuper = adminAuth.isSuperAdmin();

  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [loading, setLoading] = useState(false);

  const [pathway, setPathway] = useState("all");
  const [followUp, setFollowUp] = useState("all");
  const [funnel, setFunnel] = useState("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [drawerLead, setDrawerLead] = useState(null);
  const [questionsCache, setQuestionsCache] = useState(null);

  // Visibility gate per ADMIN_SPEC
  useEffect(() => {
    if (!adminAuth.getToken()) { navigate("/admin/login"); return; }
    if (!isSuper) {
      toast.error("Super admin access required for Wellness Gateway Leads");
      navigate("/admin");
    }
  }, [navigate, isSuper]);

  // Pre-fetch questions definition once (for label rendering in the drawer)
  useEffect(() => {
    fetch(`${API}/wellness-assessment/questions`)
      .then(r => r.json())
      .then(setQuestionsCache)
      .catch(() => {});
  }, []);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const params = useMemo(() => {
    const p = { page, page_size: pageSize };
    if (pathway !== "all") p.pathway = pathway;
    if (followUp !== "all") p.follow_up_status = followUp;
    if (funnel !== "all") p.funnel = funnel;
    if (debouncedSearch) p.q = debouncedSearch;
    return p;
  }, [page, pageSize, pathway, followUp, funnel, debouncedSearch]);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/wellness-leads/stats`);
      setStats(data);
    } catch { setStats(null); }
  }, []);

  const loadLeads = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/wellness-leads`, { params });
      setLeads(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load wellness leads");
    } finally {
      setLoading(false);
    }
  }, [params]);

  // Reset to page 1 when any filter changes
  useEffect(() => { setPage(1); }, [pathway, followUp, funnel, debouncedSearch]);
  useEffect(() => { loadLeads(); }, [loadLeads]);
  useEffect(() => { loadStats(); }, [loadStats, leads.length]);

  const updateRowStatus = async (leadId, newStatus) => {
    try {
      await api.patch(`/admin/wellness-leads/${leadId}`, { follow_up_status: newStatus });
      setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, follow_up_status: newStatus } : l)));
      loadStats();
      toast.success(`Marked as ${newStatus}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const downloadCsv = async () => {
    const qs = new URLSearchParams();
    if (pathway !== "all") qs.set("pathway", pathway);
    if (followUp !== "all") qs.set("follow_up_status", followUp);
    try {
      // Use api so the Bearer token is auto-attached
      const resp = await api.get(`/admin/wellness-leads/export.csv?${qs.toString()}`, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `wellness-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const lastPage = Math.max(1, Math.ceil(total / pageSize));

  if (!isSuper) return null; // gating effect navigates away

  const totalLeads = stats?.total ?? 0;
  const newCount = stats?.follow_up_breakdown?.new ?? 0;
  const kcisCount = stats?.pathway_breakdown?.kcis ?? 0;
  const convertedCount = stats?.follow_up_breakdown?.converted ?? 0;
  const conversionPct = totalLeads ? Math.round((convertedCount / totalLeads) * 1000) / 10 : 0;

  return (
    <div className="min-h-screen flex flex-col">
      <TopNav
        right={
          <div className="flex items-center gap-1">
            <Link to="/admin/dashboard">
              <Button variant="ghost" className="rounded-full text-slate-700">
                Dashboard
              </Button>
            </Link>
            <Button
              variant="ghost"
              className="rounded-full text-slate-700"
              onClick={() => { adminAuth.clear(); navigate("/"); }}
              data-testid="admin-logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" /> Sign out
            </Button>
          </div>
        }
      />
      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 w-full">
        <div className="kcis-fade-up">
          <div className="kcis-overline text-purple-900 flex items-center gap-2">
            <Leaf className="h-4 w-4" /> Wellness Gateway
          </div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight text-slate-900 mt-2">
            Wellness Gateway Leads
          </h1>
          <p className="text-slate-600 mt-2 max-w-2xl">
            Free, public KISSED Wellness Assessment submissions routed by KCIS scoring. Manage follow-up
            and triage which leads should be invited into the KCIS Clinical Assessment pathway.
          </p>
        </div>

        {/* KPI tiles */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-10" data-testid="wellness-kpi-tiles">
          <KpiTile label="Total leads" value={totalLeads} testid="kpi-total" />
          <KpiTile label="New (untouched)" value={newCount} testid="kpi-new" />
          <KpiTile label="KCIS-routed" value={kcisCount} testid="kpi-kcis-routed" />
          <KpiTile label="Conversion rate" value={`${conversionPct}%`} testid="kpi-conversion" />
        </div>

        {/* Filter bar */}
        <div className="mt-10 kcis-card p-5">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="text-xs text-slate-500 uppercase tracking-wide block mb-1">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  data-testid="wellness-search-input"
                  className="pl-9"
                  placeholder="Name or email…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
            <div className="min-w-[160px]">
              <label className="text-xs text-slate-500 uppercase tracking-wide block mb-1">Pathway</label>
              <Select value={pathway} onValueChange={setPathway}>
                <SelectTrigger data-testid="wellness-pathway-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PATHWAY_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[160px]">
              <label className="text-xs text-slate-500 uppercase tracking-wide block mb-1">Follow-up</label>
              <Select value={followUp} onValueChange={setFollowUp}>
                <SelectTrigger data-testid="wellness-followup-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FOLLOW_UP_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[200px]">
              <label className="text-xs text-slate-500 uppercase tracking-wide block mb-1">Recommended funnel</label>
              <Select value={funnel} onValueChange={setFunnel}>
                <SelectTrigger data-testid="wellness-funnel-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FUNNEL_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={downloadCsv} className="kcis-btn-primary" data-testid="wellness-export-csv-btn">
              <Download className="h-4 w-4 mr-2" /> Export CSV
            </Button>
          </div>
        </div>

        {/* Leads table */}
        <div className="mt-6 kcis-card overflow-hidden">
          <div className="overflow-x-auto">
            <Table data-testid="wellness-leads-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Created</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Pathway</TableHead>
                  <TableHead>Funnel</TableHead>
                  <TableHead>Readiness</TableHead>
                  <TableHead>Follow-up</TableHead>
                  <TableHead>Clicked</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={9} className="py-12 text-center text-slate-500">
                    <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…
                  </TableCell></TableRow>
                ) : leads.length === 0 ? (
                  <TableRow><TableCell colSpan={9} className="py-12 text-center text-slate-500">
                    No wellness leads match your filters yet.
                  </TableCell></TableRow>
                ) : leads.map((l) => (
                  <TableRow key={l.id} className="hover:bg-purple-50/30" data-testid={`wellness-lead-row-${l.id}`}>
                    <TableCell className="whitespace-nowrap text-xs text-slate-600">{fmtDate(l.created_at)}</TableCell>
                    <TableCell className="font-medium text-slate-900">{l.first_name}</TableCell>
                    <TableCell><a href={`mailto:${l.email}`} className="text-purple-900 underline">{l.email}</a></TableCell>
                    <TableCell><PathwayPill pathway={l.pathway_assigned} /></TableCell>
                    <TableCell className="text-xs">{l.recommended_funnel || "—"}</TableCell>
                    <TableCell>
                      <ReadinessBar value={l.q16_readiness_score} />
                    </TableCell>
                    <TableCell>
                      <Select value={l.follow_up_status || "new"} onValueChange={(v) => updateRowStatus(l.id, v)}>
                        <SelectTrigger className="h-8 w-32" data-testid={`row-status-${l.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {FOLLOW_UP_OPTIONS.filter(o => o.value !== "all").map(o => (
                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-xs text-slate-600">{l.funnel_redirected_to || "—"}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDrawerLead(l.id)}
                        data-testid={`view-lead-${l.id}`}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {/* Pagination */}
          <div className="flex items-center justify-between px-5 py-3 border-t bg-slate-50/40">
            <div className="text-xs text-slate-500">
              {total === 0 ? "0 leads" : `Showing ${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} of ${total}`}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="page-prev">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-xs text-slate-600">Page {page} / {lastPage}</span>
              <Button variant="outline" size="sm" disabled={page >= lastPage} onClick={() => setPage((p) => Math.min(lastPage, p + 1))} data-testid="page-next">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </main>

      <LeadDrawer
        leadId={drawerLead}
        open={!!drawerLead}
        onClose={() => setDrawerLead(null)}
        questionsCache={questionsCache}
        onPatched={(patched) => {
          setLeads((prev) => prev.map(l => l.id === patched.id ? { ...l, ...patched } : l));
          loadStats();
        }}
      />

      <Footer />
    </div>
  );
}

function KpiTile({ label, value, testid }) {
  return (
    <div className="kcis-card p-6" data-testid={testid}>
      <div className="kcis-overline">{label}</div>
      <div className="font-display text-3xl text-slate-900 mt-3">{value}</div>
    </div>
  );
}

function ReadinessBar({ value }) {
  const v = Math.max(0, Math.min(5, Number(value) || 0));
  return (
    <div className="flex items-center gap-2">
      <div className="w-16 h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full bg-purple-900" style={{ width: `${(v / 5) * 100}%` }} />
      </div>
      <span className="text-xs text-slate-700 tabular-nums">{v || "—"}</span>
    </div>
  );
}
